import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const RegularSupportContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const TextContent = styled.div`
  line-height: 1.8;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  .highlight {
    background: ${props => props.theme.colors.backgroundLight};
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    border-left: 4px solid ${props => props.theme.colors.primary};
  }
`;

const ExampleSection = styled.div`
  background: ${props => props.theme.colors.gradients.card};
  padding: 2rem;
  border-radius: 15px;
  margin: 2rem 0;
  
  h3 {
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
  
  p {
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.textLight};
  }
`;

const ContactSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const ContactCard = styled.div`
  background: ${props => props.theme.colors.gradients.card};
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  .contact-details {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    
    .contact-item {
      text-align: center;
      
      .icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .label {
        font-size: 0.9rem;
        color: ${props => props.theme.colors.textLight};
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
        
        a {
          color: ${props => props.theme.colors.primary};
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const RegularSupport = () => {
  return (
    <RegularSupportContainer>
      <Helmet>
        <title>Regelmäßige Tiergestützte Förderung - Der Ponystall</title>
        <meta name="description" content="Regelmäßige tiergestützte Förderung für Institutionen. Wöchentliche, 14tägige oder monatliche Termine mit individuellen Lernzielen und spielerischer Umsetzung." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Regelmäßige Tiergestützte Förderung
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentCard>
            <SectionTitle>Beispiel einer Gruppenstunde</SectionTitle>
            <TextContent>
              <p>
                Nach der Begrüßung werden die Ponys von den Kindern aus der Box geführt und an ihrem Platz angebunden. 
                Die Putzkästen werden aus der Sattelkammer geholt. Jedes Pony wird intensiv geputzt und mit verschiedenen 
                Bürsten gesäubert, die Hufe werden ausgekratzt. Anschließend werden die Putzkästen weggeräumt. 
                Die Decken und Gurte der Ponys werden geholt und angelegt. Das gemeinsame Fegen der Stallgasse wird zum Ritual.
              </p>
              
              <p>
                Nun kann es losgehen. Jedes Kind bekommt einen Helm und es wird festgelegt wer die Ponys in die 
                Bewegungshalle führt. Gemeinsam gehen wir in die Halle und tauchen ein in eine Phantasiewelt des Spiels und des Reitens.
              </p>
              
              <ExampleSection>
                <h3>Das Zauberwald-Abenteuer</h3>
                <p>
                  Die Königin hat ihre Tiere im Zauberwald verloren. Die Ritter und Knappen bekommen die Aufgabe 
                  die Tiere zu suchen und bewaffnet mit ihrer Lanze loszuziehen. Aber Vorsicht! Es lauern Gefahren. 
                  Gemeinsam finden wir den Weg und die Tiere werden zur Königin ins Schloss gebracht....
                </p>
                <p>
                  Das Spiel wird mehrmals gespielt, damit jedes Kind auch die Möglichkeit hat sich in verschiedenen 
                  Spielrollen und Aufgaben zu erfahren.
                </p>
              </ExampleSection>
              
              <p>
                Bei Bedarf wird zwischendurch eine Trink- und Snackpause eingelegt.
              </p>
              
              <p>
                Nach Beendigung der Spielphase gehen wir wieder in den Stall. Die Ponys werden abgesattelt, 
                gefüttert und gelobt. Es ist noch kurz Zeit für Streicheleinheiten, Gespräche und Tiere bestaunen 
                und dann ist Verabschiedung bis zum nächsten Mal. 🙂
              </p>
            </TextContent>
          </ContentCard>

          <ContentCard>
            <SectionTitle>Regelmäßige Tiergestützte Förderung</SectionTitle>
            <TextContent>
              <p>
                Die regelmäßige tiergestützte Förderung kann auf Wunsch wöchentlich, 14tägig oder monatlich 
                stattfinden und dauert ca. 1,5 bis 2 Stunden.
              </p>
              
              <div className="highlight">
                <p>
                  Je nach Gruppe werden unterschiedliche Lern- oder Förderziele ermittelt und in geeignete Spiele 
                  ausgearbeitet. Durch die Planung der Stunden unter Einbeziehung individueller Lernaspekte sowie 
                  die Schaffung einer motivierenden, kindgerechten Lernumgebung kann mit Hilfe der Ponys ein 
                  aktiver Prozess der Wissensaufnahme stattfinden.
                </p>
              </div>
              
              <p>
                Die Regelmäßigkeit und der gleichbleibenden Ablauf der Stunden gibt den Kindern Struktur, 
                Sicherheit und ermöglicht eigenständiges Handeln.
              </p>
            </TextContent>
          </ContentCard>
        </Container>
      </ContentSection>

      <ContactSection>
        <Container>
          <ContactCard>
            <h3>Kontakt</h3>
            <div className="contact-details">
              <div className="contact-item">
                <div className="icon">📍</div>
                <div className="label">Adresse</div>
                <div className="value">Weidenweg 2, 26683 Saterland</div>
              </div>
              <div className="contact-item">
                <div className="icon">📞</div>
                <div className="label">Telefon</div>
                <div className="value">
                  <a href="tel:01621353794">0162 1353794</a> / <a href="tel:044987522">04498 7522</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="icon">✉️</div>
                <div className="label">E-Mail</div>
                <div className="value">
                  <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a>
                </div>
              </div>
            </div>
          </ContactCard>
        </Container>
      </ContactSection>
    </RegularSupportContainer>
  );
};

export default RegularSupport;
